/**
 * PIVOT01: the officer pivot block as `GET /cases/{id}/pivots` returns it (`pivot/1`). A pivot is a lead: the model chose
 * what to look at, the engine read the pages and recorded them, and every statement cites a record fact, a pivot row or a
 * hash. Nothing in it is a label, a notice row or a matrix row.
 */
export type PivotStatus = 'answered' | 'stopped' | 'off' | 'error';

export interface PivotStatement {
  id: string;
  text: string;
  cites: string[];
  grade: 'lead';
}

export interface PivotCited {
  kind: 'fact' | 'pivot' | 'hash';
  block?: string;
  tool?: string;
  round?: number;
  text?: string;
  facts?: string[];
}

export interface PivotRead {
  seq: number;
  kind: 'transactions' | 'transaction';
  chain: string;
  address: string;
  provider?: string | null;
  source?: string | null;
  at?: string | null;
  rows?: number;
  cut?: boolean;
  error?: string | null;
}

/** What a row says about the transfer it describes (PIVOT02): payer, payee, chain, token, count, total, and for a ledger row the transfers. */
export interface PivotRowFields {
  from?: string;
  to?: string;
  chain?: string;
  token?: string | null;
  count?: number;
  total?: number | null;
  /** `ledger` when the row came from the case transfer ledger rather than a live page read. */
  source?: string;
  /** A ledger row that groups the transfers of one payer, payee and token. */
  pair?: boolean;
  kind?: string;
  transfers?: Array<{ tx_hash: string; value?: number | null; token?: string | null; timestamp?: string | null }>;
  [key: string]: unknown;
}

export interface PivotRow {
  id: string;
  round: number;
  tool: string;
  text: string;
  hashes: string[];
  fields?: PivotRowFields;
}

/** The rows the officer has promoted to the graph: `block.promoted`. */
export interface PivotPromoted {
  row_id: string;
  job_id?: string | null;
  event_id?: string | null;
}

export interface PivotLedgerEntry {
  round: number;
  tool: string;
  arguments: Record<string, unknown>;
  status: 'ok' | 'partial' | 'refused' | 'error';
  reason?: string | null;
  stopped?: string | null;
  /** E.g. a chain the model named that the record corrected. */
  notes?: string[];
  reads: PivotRead[];
  rows: PivotRow[];
  /** A `ledger` call: how many ledger rows matched and how many came back. */
  ledger?: { total?: number; returned?: number; limit?: number };
}

export interface PivotCounts {
  statements?: number;
  dropped_uncited?: number;
  dropped_unknown_cite?: number;
  dropped_malformed?: number;
}

export interface PivotBlock {
  schema: 'pivot/1';
  grade: 'lead';
  pivot_id: string;
  run_id?: string | null;
  run_event_id?: string | null;
  question: string;
  status: PivotStatus;
  reason?: string | null;
  created_at?: string | null;
  budget?: { seconds?: number; pages?: number; rounds?: number; max_usd?: number };
  used?: { seconds?: number; pages?: number; rounds?: number; usd?: number };
  statements?: PivotStatement[];
  counts?: PivotCounts;
  cited?: Record<string, PivotCited>;
  ledger?: PivotLedgerEntry[];
  rows?: PivotRow[];
  /** What the answer rests on and what it does not cover (required of an answer since PIVOT02). */
  coverage_note?: string | null;
  promoted?: PivotPromoted[];
}

/** A row can be put on the graph when it names a payer, a payee and a chain. */
export function isPromotableRow(r: PivotRow): boolean {
  const f = r.fields;
  return !!(f && typeof f.from === 'string' && f.from && typeof f.to === 'string' && f.to && typeof f.chain === 'string' && f.chain);
}

/** The two budgets the question box shows (the server cuts a figure to its ceiling). */
export const PIVOT_DEFAULT_SECONDS = 60;
export const PIVOT_DEFAULT_PAGES = 40;
export const PIVOT_MAX_SECONDS = 180;
export const PIVOT_MAX_PAGES = 100;
